/**
 * Shared state connecting criterion feedback controls to PDF proof capture.
 * @module medicalNecessityAnalysis/context/PdfProofCaptureContext
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem } from "../../../data-model/nccnGuidelines";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { uploadToFirebaseStorage } from "../../../utils/fileUpload";
import { ReviewPdfProofModal } from "../components/ReviewPdfProofModal";
import {
  PdfProof,
  PdfProofCaptureMode,
  PdfProofDraft,
  PdfProofPreview,
} from "../types/pdfProof";

/** Analytics location for the criterion whose proof is being captured. */
interface ProofAnalyticsContext {
  guideline: NCCNEvaluationRecord;
  guidelineIndex: number;
  item: EvaluationItem;
  criterionIndex: number;
  /**
   * Sets the action id of the most recent capture click (annotation /
   * screenshot / retake / reselect), consumed by the ensuing saved event.
   */
  setPendingActionId: (actionId: string | undefined) => void;
  /**
   * Returns the action id of the most recent capture click, consumed by the
   * ensuing saved event.
   */
  getPendingActionId: () => string | undefined;
  trackAnnotationSaved: (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    comment: string,
    actionId?: string,
  ) => void;
  trackScreenshotSaved: (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    comment: string,
    actionId?: string,
  ) => void;
  trackScreenshotRetakeClicked: (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ) => string;
  trackAnnotationReselectClicked: (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
  ) => string;
}

/** Values exposed to the PDF panel and active criterion form. */
interface PdfProofCaptureContextValue {
  mode: PdfProofCaptureMode;
  proofs: PdfProof[];
  isReviewing: boolean;
  isUploading: boolean;
  beginSession: (proofs: PdfProof[]) => void;
  startTextCapture: () => void;
  startScreenshotCapture: () => void;
  receiveDraft: (draft: PdfProofDraft) => void;
  editProof: (proofId: string) => void;
  removeProof: (proofId: string) => void;
  cancelCapture: () => void;
  endSession: () => void;
  /**
   * Registers the criterion + analytics handlers for the active capture so
   * the provider can fire saved / retake / reselect events.
   */
  setAnalyticsContext: (context: ProofAnalyticsContext | null) => void;
}

const PdfProofCaptureContext =
  createContext<PdfProofCaptureContextValue | null>(null);

/** Props for {@link PdfProofCaptureProvider}. */
interface PdfProofCaptureProviderProps {
  /** Order used to namespace uploaded screenshot crops. */
  orderId: string;
  /** Medical Necessity screen content. */
  children: React.ReactNode;
}

/**
 * Coordinates PDF capture, annotation review, and screenshot upload.
 *
 * @param props - Current order ID and screen children
 * @returns Provider wrapping the Medical Necessity PDF and criterion panel
 */
export const PdfProofCaptureProvider: React.FC<
  PdfProofCaptureProviderProps
> = ({ orderId, children }) => {
  const [mode, setMode] = useState<PdfProofCaptureMode>("idle");
  const [proofs, setProofs] = useState<PdfProof[]>([]);
  const [draft, setDraft] = useState<PdfProofDraft | null>(null);
  const [editedProofId, setEditedProofId] = useState<string | null>(null);
  const [editedImageUrl, setEditedImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  // The criterion + analytics handlers for the active capture, registered by
  // the open CriterionFeedbackForm. Null when no form is capturing.
  const analyticsContextRef = useRef<ProofAnalyticsContext | null>(null);

  const setAnalyticsContext = useCallback(
    (context: ProofAnalyticsContext | null) => {
      analyticsContextRef.current = context;
    },
    [],
  );

  const editedProof =
    proofs.find((proof) => proof.id === editedProofId) ?? null;

  /** Closes the dialog and forgets the reference being edited. */
  const closeReview = useCallback(() => {
    setDraft(null);
    setEditedProofId(null);
  }, []);

  useEffect(
    () => () => {
      if (draft?.type === "pdf_screenshot") {
        URL.revokeObjectURL(draft.previewUrl);
      }
    },
    [draft],
  );

  // A saved screenshot only stores its storage path, so the dialog needs a
  // freshly signed download URL before it can show the crop again.
  useEffect(() => {
    const imagePath =
      editedProof?.type === "pdf_screenshot"
        ? editedProof.imagePath
        : undefined;
    if (!imagePath) {
      setEditedImageUrl("");
      return undefined;
    }
    let active = true;
    void getFileDownloadUrl(imagePath).then((url) => {
      if (active) setEditedImageUrl(url);
    });
    return () => {
      active = false;
    };
  }, [editedProof]);

  /** Starts a form session from its already-persisted references. */
  const beginSession = useCallback(
    (initialProofs: PdfProof[]) => {
      closeReview();
      setMode("idle");
      setProofs(initialProofs);
    },
    [closeReview],
  );

  /** Accepts a renderer capture and opens the annotation review modal. */
  const receiveDraft = useCallback((nextDraft: PdfProofDraft) => {
    setDraft(nextDraft);
    setMode("idle");
  }, []);

  /** Enters glyph-based text selection mode. */
  const startTextCapture = useCallback(() => {
    closeReview();
    setMode("text");
  }, [closeReview]);

  /** Enters rectangular PDF screenshot mode. */
  const startScreenshotCapture = useCallback(() => {
    closeReview();
    setMode("screenshot");
  }, [closeReview]);

  /** Reopens a confirmed reference so its comment or capture can be changed. */
  const editProof = useCallback((proofId: string) => {
    setDraft(null);
    setMode("idle");
    setEditedProofId(proofId);
  }, []);

  /** Removes one confirmed reference from the active form session. */
  const removeProof = useCallback(
    (proofId: string) => {
      if (proofId === editedProofId) closeReview();
      setProofs((current) => current.filter((proof) => proof.id !== proofId));
    },
    [closeReview, editedProofId],
  );

  /** Stops PDF interaction without clearing confirmed references. */
  const cancelCapture = useCallback(() => {
    closeReview();
    setMode("idle");
  }, [closeReview]);

  /** Clears all temporary state when the criterion form closes. */
  const endSession = useCallback(() => {
    closeReview();
    setMode("idle");
    setProofs([]);
  }, [closeReview]);

  /** Re-enters the capture mode matching the reference under review. */
  const selectAgain = useCallback(() => {
    const reviewedType = draft?.type ?? editedProof?.type;
    const analytics = analyticsContextRef.current;
    if (analytics) {
      const actionId =
        reviewedType === "pdf_screenshot"
          ? analytics.trackScreenshotRetakeClicked(
              analytics.guideline,
              analytics.guidelineIndex,
              analytics.item,
              analytics.criterionIndex,
            )
          : analytics.trackAnnotationReselectClicked(
              analytics.guideline,
              analytics.guidelineIndex,
              analytics.item,
              analytics.criterionIndex,
            );
      analytics.setPendingActionId(actionId);
    }
    setDraft(null);
    setMode(reviewedType === "pdf_screenshot" ? "screenshot" : "text");
  }, [draft, editedProof]);

  /**
   * Confirms the current draft and uploads screenshot content when needed.
   *
   * Replaces the reference under edit when the reviewer recaptured it, so the
   * new selection keeps its position in the list.
   *
   * @param comment - Optional reviewer annotation
   */
  const confirmDraft = useCallback(
    async (comment: string): Promise<void> => {
      if (!draft) return;
      setIsUploading(true);
      try {
        const id = uuidv4();
        let proof: PdfProof;
        if (draft.type === "pdf_text") {
          proof = {
            id,
            type: draft.type,
            text: draft.text,
            region: draft.region,
            comment: comment.trim() || undefined,
          };
        } else {
          const imagePath = `${orderId}/screenshots/${id}.png`;
          await uploadToFirebaseStorage({
            file: draft.blob,
            storagePath: imagePath,
            fileName: `${id}.png`,
          });
          proof = {
            id,
            type: draft.type,
            imagePath,
            region: draft.region,
            comment: comment.trim() || undefined,
          };
        }
        setProofs((current) =>
          editedProofId
            ? current.map((existing) =>
                existing.id === editedProofId ? proof : existing,
              )
            : [...current, proof],
        );
        const analytics = analyticsContextRef.current;
        if (analytics) {
          const actionId = analytics.getPendingActionId();
          analytics.setPendingActionId(undefined);
          if (proof.type === "pdf_text") {
            analytics.trackAnnotationSaved(
              analytics.guideline,
              analytics.guidelineIndex,
              analytics.item,
              analytics.criterionIndex,
              comment,
              actionId,
            );
          } else {
            analytics.trackScreenshotSaved(
              analytics.guideline,
              analytics.guidelineIndex,
              analytics.item,
              analytics.criterionIndex,
              comment,
              actionId,
            );
          }
        }
        closeReview();
      } catch {
        toast.error("Failed to save PDF reference");
      } finally {
        setIsUploading(false);
      }
    },
    [closeReview, draft, editedProofId, orderId],
  );

  /**
   * Saves the dialog contents for either a new capture or an edited reference.
   *
   * @param comment - Reviewer annotation entered in the dialog
   */
  const saveReview = useCallback(
    (comment: string): void => {
      if (draft) {
        void confirmDraft(comment);
        return;
      }
      if (!editedProofId) return;
      setProofs((current) =>
        current.map((proof) =>
          proof.id === editedProofId
            ? { ...proof, comment: comment.trim() || undefined }
            : proof,
        ),
      );
      const analytics = analyticsContextRef.current;
      if (analytics && editedProof) {
        const actionId = analytics.getPendingActionId();
        analytics.setPendingActionId(undefined);
        if (editedProof.type === "pdf_text") {
          analytics.trackAnnotationSaved(
            analytics.guideline,
            analytics.guidelineIndex,
            analytics.item,
            analytics.criterionIndex,
            comment,
            actionId,
          );
        } else {
          analytics.trackScreenshotSaved(
            analytics.guideline,
            analytics.guidelineIndex,
            analytics.item,
            analytics.criterionIndex,
            comment,
            actionId,
          );
        }
      }
      closeReview();
    },
    [closeReview, confirmDraft, draft, editedProof, editedProofId],
  );

  const preview = useMemo<PdfProofPreview | null>(() => {
    if (draft)
      return draft.type === "pdf_text"
        ? { type: "pdf_text", text: draft.text }
        : { type: "pdf_screenshot", imageUrl: draft.previewUrl };
    if (!editedProof) return null;
    return editedProof.type === "pdf_text"
      ? { type: "pdf_text", text: editedProof.text ?? "" }
      : { type: "pdf_screenshot", imageUrl: editedImageUrl };
  }, [draft, editedImageUrl, editedProof]);

  // Recapturing from the dialog swaps it out for the PDF, so the dialog stays
  // hidden until the reviewer is back in idle mode with something to confirm.
  const isReviewing = mode === "idle" && preview !== null;

  const value = useMemo<PdfProofCaptureContextValue>(
    () => ({
      mode,
      proofs,
      isReviewing,
      isUploading,
      beginSession,
      startTextCapture,
      startScreenshotCapture,
      receiveDraft,
      editProof,
      removeProof,
      cancelCapture,
      endSession,
      setAnalyticsContext,
    }),
    [
      beginSession,
      cancelCapture,
      editProof,
      endSession,
      isReviewing,
      isUploading,
      mode,
      proofs,
      receiveDraft,
      removeProof,
      setAnalyticsContext,
      startScreenshotCapture,
      startTextCapture,
    ],
  );

  return (
    <PdfProofCaptureContext.Provider value={value}>
      {children}
      {isReviewing && preview && (
        <ReviewPdfProofModal
          preview={preview}
          initialComment={editedProof?.comment ?? ""}
          isEditing={editedProofId !== null}
          isSaving={isUploading}
          onCancel={closeReview}
          onSelectAgain={selectAgain}
          onSave={saveReview}
        />
      )}
    </PdfProofCaptureContext.Provider>
  );
};

/**
 * Returns the active Medical Necessity PDF proof capture session.
 *
 * @returns Shared PDF proof capture state and actions
 * @throws Error when used outside {@link PdfProofCaptureProvider}
 */
export const usePdfProofCapture = (): PdfProofCaptureContextValue => {
  const context = useContext(PdfProofCaptureContext);
  if (!context) {
    throw new Error(
      "usePdfProofCapture must be used within PdfProofCaptureProvider",
    );
  }
  return context;
};
